import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { ArrowLeft, CheckCircle, Clock, User, Bot, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';

const Submissions = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiGradingStatus, setAiGradingStatus] = useState({});
  const [triggeringAI, setTriggeringAI] = useState({});
  const [hasPremium, setHasPremium] = useState(false);
  const [bulkGrading, setBulkGrading] = useState(false);
  const [bulkJob, setBulkJob] = useState(null);

  useEffect(() => {
    fetchData();
    checkPremiumAccess();
  }, [examId]);

  const fetchData = async () => {
    try {
      const [examRes, submissionsRes] = await Promise.all([
        api.get(`/exams/${examId}`),
        api.get(`/submissions/exam/${examId}`),
      ]);
      setExam(examRes.data);
      setSubmissions(submissionsRes.data);
      
      // Fetch AI grading status for each submission
      const statusPromises = submissionsRes.data.map(sub => 
        api.get(`/ai-grading/status/${sub.id}`).catch(() => ({ data: { status: 'not_started' } }))
      );
      const statuses = await Promise.all(statusPromises);
      const statusMap = {};
      submissionsRes.data.forEach((sub, idx) => {
        statusMap[sub.id] = statuses[idx].data;
      });
      setAiGradingStatus(statusMap);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkPremiumAccess = async () => {
    try {
      const res = await api.get('/school-admin/subscription');
      // API returns 'tier' not 'subscription_tier'
      setHasPremium(res.data.tier === 'premium' || res.data.ai_grading === true);
    } catch (err) {
      // If endpoint doesn't exist or fails, assume no premium
      setHasPremium(false);
    }
  };

  const triggerBulkAIGrading = async () => {
    setBulkGrading(true);
    try {
      const res = await api.post(`/ai-grading/bulk-grade/${examId}`);
      toast.success(res.data.message);
      setBulkJob(res.data);
      
      // Poll for bulk job completion
      pollBulkGradingStatus(res.data.bulk_job_id);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to start bulk AI grading';
      toast.error(detail);
      setBulkGrading(false);
    }
  };

  const pollBulkGradingStatus = async (bulkJobId) => {
    const maxAttempts = 120; // 4 minutes max
    let attempts = 0;
    
    const poll = async () => {
      try {
        const res = await api.get(`/ai-grading/bulk-status/${bulkJobId}`);
        setBulkJob(res.data);
        
        if (res.data.status === 'completed') {
          toast.success(`Bulk grading completed: ${res.data.completed} graded, ${res.data.failed} failed`);
          setBulkGrading(false);
          // Refresh data to show updated statuses
          fetchData();
          return;
        }
        
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          toast.error('Bulk grading timed out. Check status manually.');
          setBulkGrading(false);
        }
      } catch (err) {
        console.error('Error polling bulk status:', err);
        setBulkGrading(false);
      }
    };
    
    poll();
  };

  const getUngradedCount = () => {
    return submissions.filter(s => {
      const status = aiGradingStatus[s.id];
      return !status || status.status === 'not_started' || status.status === 'failed';
    }).length;
  };

  const triggerAIGrading = async (submissionId) => {
    setTriggeringAI(prev => ({ ...prev, [submissionId]: true }));
    try {
      await api.post(`/ai-grading/grade/${submissionId}`);
      toast.success('AI grading started');
      setAiGradingStatus(prev => ({ ...prev, [submissionId]: { status: 'grading' } }));
      
      // Poll for completion
      pollGradingStatus(submissionId);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to start AI grading';
      toast.error(detail);
    } finally {
      setTriggeringAI(prev => ({ ...prev, [submissionId]: false }));
    }
  };

  const pollGradingStatus = async (submissionId) => {
    const maxAttempts = 30;
    let attempts = 0;
    
    const poll = async () => {
      try {
        const res = await api.get(`/ai-grading/status/${submissionId}`);
        setAiGradingStatus(prev => ({ ...prev, [submissionId]: res.data }));
        
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          if (res.data.status === 'completed') {
            toast.success('AI grading completed');
          } else {
            toast.error('AI grading failed');
          }
          return;
        }
        
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        }
      } catch (err) {
        console.error('Error polling status:', err);
      }
    };
    
    poll();
  };

  const getAIStatusBadge = (submissionId) => {
    const status = aiGradingStatus[submissionId];
    if (!status || status.status === 'not_started') {
      return null;
    }
    
    switch (status.status) {
      case 'grading':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full">
            <Loader2 className="w-3 h-3 animate-spin" />
            AI Grading...
          </span>
        );
      case 'completed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-600 text-xs rounded-full">
            <Bot className="w-3 h-3" />
            AI: {status.ai_grade} ({status.ai_percentage}%)
          </span>
        );
      case 'reviewed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-600 text-xs rounded-full">
            <Bot className="w-3 h-3" />
            Review in progress
          </span>
        );
      case 'finalized':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-50 text-green-600 text-xs rounded-full">
            <CheckCircle className="w-3 h-3" />
            AI Graded
          </span>
        );
      case 'failed':
        return (
          <span className="px-2 py-1 bg-red-50 text-red-600 text-xs rounded-full">
            AI Failed
          </span>
        );
      default:
        return null;
    }
  };

  // Check if exam has structured questions (AI grading only for structured)
  const hasStructuredQuestions = exam?.questions?.some(q => q.question_type !== 'mcq');

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/teacher/dashboard')}
            className="flex items-center gap-2 text-slate-600 hover:text-indigo-700 transition-colors mb-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-slate-900">
              {exam?.title || 'Exam'} - Submissions
            </h1>
            <div className="flex items-center gap-3">
              {hasPremium && hasStructuredQuestions && (
                <>
                  <span className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 rounded-full text-sm font-medium">
                    <Bot className="w-4 h-4" />
                    AI Grading Available
                  </span>
                  {submissions.length > 0 && (
                    <button
                      data-testid="bulk-ai-grade-btn"
                      onClick={triggerBulkAIGrading}
                      disabled={bulkGrading || getUngradedCount() === 0}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                    >
                      {bulkGrading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Grading {bulkJob?.completed || 0}/{bulkJob?.submissions_to_grade || '...'}
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4" />
                          AI Grade All ({getUngradedCount()})
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 md:p-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <User className="w-6 h-6 text-indigo-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Submissions</p>
                <p className="text-2xl font-bold text-slate-900">{submissions.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-emerald-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Graded</p>
                <p className="text-2xl font-bold text-slate-900">
                  {submissions.filter(s => s.is_graded).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-xl border border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Bot className="w-6 h-6 text-purple-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">AI Graded</p>
                <p className="text-2xl font-bold text-slate-900">
                  {Object.values(aiGradingStatus).filter(s => s.status === 'completed' || s.status === 'finalized').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bulk Grading Progress */}
        {bulkGrading && bulkJob && (
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                </div>
                <div>
                  <h3 className="font-semibold text-purple-900">Bulk AI Grading in Progress</h3>
                  <p className="text-sm text-purple-600">
                    Grading {bulkJob.completed || 0} of {bulkJob.submissions_to_grade || 0} submissions
                  </p>
                </div>
              </div>
              <span className="text-lg font-bold text-purple-700">
                {bulkJob.progress_percentage || 0}%
              </span>
            </div>
            <div className="w-full bg-purple-200 rounded-full h-3">
              <div 
                className="bg-gradient-to-r from-purple-500 to-indigo-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${bulkJob.progress_percentage || 0}%` }}
              />
            </div>
            {bulkJob.failed > 0 && (
              <p className="text-sm text-red-600 mt-2">
                {bulkJob.failed} submission(s) failed to grade
              </p>
            )}
          </div>
        )}

        {/* Submissions List */}
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">Submissions</h2>
          
          {loading ? (
            <div className="text-center py-12 text-slate-500">Loading submissions...</div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No submissions yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission) => {
                const aiStatus = aiGradingStatus[submission.id];
                const canTriggerAI = hasPremium && hasStructuredQuestions && 
                  (!aiStatus || aiStatus.status === 'not_started' || aiStatus.status === 'failed');
                const canReviewAI = aiStatus && ['completed', 'reviewed'].includes(aiStatus.status);
                
                return (
                  <div
                    key={submission.id}
                    data-testid={`submission-${submission.id}`}
                    className="bg-white border border-slate-200 rounded-xl p-6 hover:border-indigo-200 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-lg font-semibold text-slate-900">
                            {submission.student_name}
                          </h3>
                          {getAIStatusBadge(submission.id)}
                        </div>
                        <p className="text-sm text-slate-600">
                          Submitted on {new Date(submission.submitted_at).toLocaleString()}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {submission.is_graded ? (
                          <div className="text-right">
                            <div className="text-xl font-bold text-indigo-700">
                              {submission.marks_allocated} marks
                            </div>
                            <div className="text-sm text-emerald-600">Grade: {submission.grade}</div>
                            {submission.ai_assisted && (
                              <div className="text-xs text-purple-500 flex items-center gap-1 justify-end">
                                <Bot className="w-3 h-3" />
                                AI Assisted
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="px-3 py-1 bg-amber-50 text-amber-700 text-sm font-medium rounded-full">
                            Pending
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          {/* AI Grade Button */}
                          {canTriggerAI && (
                            <button
                              data-testid={`ai-grade-btn-${submission.id}`}
                              onClick={() => triggerAIGrading(submission.id)}
                              disabled={triggeringAI[submission.id]}
                              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium rounded-lg transition-all disabled:opacity-50"
                            >
                              {triggeringAI[submission.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Bot className="w-4 h-4" />
                              )}
                              AI Grade
                            </button>
                          )}
                          
                          {/* Review AI Button */}
                          {canReviewAI && (
                            <button
                              data-testid={`review-ai-btn-${submission.id}`}
                              onClick={() => navigate(`/teacher/ai-grading/${submission.id}`)}
                              className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-all"
                            >
                              <Bot className="w-4 h-4" />
                              Review AI
                            </button>
                          )}
                          
                          {/* Manual Grade Button */}
                          <button
                            data-testid={`grade-btn-${submission.id}`}
                            onClick={() => navigate(`/teacher/grade/${submission.id}`)}
                            className="px-6 py-2.5 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all"
                          >
                            {submission.is_graded ? 'Review' : 'Grade'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Submissions;
